<template>
  <div 
    class="fts-file-content" 
    :class="[wrapperClass, fileContentClass]" 
    :style="wrapperStyle" 
    @mouseenter="mouseHover = true" 
    @mouseleave="mouseHover = false"
  >
    <!-- image -->
    <template v-if="fileType === 'image'">
      <img :src="filePath" @click="openLightbox">
      <div class="file-remove" @click.stop="removeFile" v-if="editable">
        <font-awesome-icon :icon="['fas', 'times']" />
      </div>
    </template>
    <!-- file -->
    <template v-else-if="fileType === 'file'">
      <div class="file-extension" :style="{ backgroundColor : fileColor }">{{fileExtension}}</div>
      <!-- file:image -->
      <a
        class="file-name"
        :href="filePath"
        target="_blank"
        tabindex="-1"
        @click.prevent="openLightbox"
        v-if="isImageFile"
      >
        {{fileNameWithoutExtension}}
      </a>
      <!-- file:default -->
      <a
        class="file-name"
        :href="filePath"
        target="_blank"
        tabindex="-1"
        v-else
      >
        {{fileNameWithoutExtension}}
      </a>
      <font-awesome-icon class="file-remove-action" :icon="['fas', 'times']" fixed-width @click.stop="removeFile" v-if="editable" />
    </template>
    <!-- attachment (image) -->
    <div class="atch-image-content" v-else-if="isImageFile" @click="openLightbox">
      <img :src="filePath">
    </div>
    <!-- attachment (file) -->
    <a
      class="atch-file-content"
      :href="filePath"
      target="_blank"
      tabindex="-1"
      v-else
    >
      <div class="atch-file-extension" >
        <i class="atch-file-extension__icon fts-icon-file"></i>
        <div class="atch-file-extension__name" :style="{ background: fileColor }">{{fileExtension}}</div>
      </div>
      <div class="file-info">
        <fts-tippy
          :content="fileName"
          :options="{ delay: [500 , 0] }"
          trigger-class="file-name"
          class="file-info__header"
        >
          <template #trigger>{{fileNameWithoutExtension}}</template>
        </fts-tippy>   
        <div class="file-size" v-if="getfileSize">{{getfileSize}}</div>
      </div>
      <fts-transition-fade>
        <div class="file-actions" v-if="mouseHover && downloadable">
          <a class="file-action" :href="`${filePath}?download=true`" tabindex="-1">
            <font-awesome-icon :icon="['fas', 'cloud-download-alt']" fixed-width  />  
          </a>
        </div>
      </fts-transition-fade>
    </a>
    <!-- attachment (image & file) action -->
    <template v-if="fileType !== 'image' && fileType !== 'file'">
      <!-- remove -->
      <div class="file-remove" @click.prevent="removeFile" v-if="editable" >
        <font-awesome-icon :icon="['fas', 'times']" />  
      </div>
      <!-- on progress -->
      <template v-if="fileProgress">
        <!-- progress percent -->
        <div class="file-progress">
          <div class="file-progress-bar" :style="{width: `${100 - fileProgress}%`}"></div>
          <div class="file-progress-percent" v-if="getProgressPercentage">
            {{getProgressPercentage}}%
          </div>
        </div>
        <!-- cancel upload -->
        <div class="file-remove" @click.prevent="cancelUpload" v-if="cancellable">
          <font-awesome-icon :icon="['fas', 'times']" />
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import commons from '../../commons/commons';
import FTSTippy from '../FTSTippy';
import FTSTransitionFade from '../transition/FTSTransitionFade';

export default {
  props: {
    // Global
    fileName: String,
    fileType: String,
    filePath: String,
    fileContentClass: String,
    fileActive: Boolean,
    fileProgress: String,
    fileSize: Number,
    editable: Boolean,
    cancellable: Boolean,
    downloadable: Boolean,
    // Image
    imageWidth: {
      type: [Number, String],
      default: 100
    },
    imageHeight: {
      type: [Number, String],
      default: 80
    },
    imageContain: Boolean,
  },
  components: {
    "fts-tippy": FTSTippy,
    "fts-transition-fade": FTSTransitionFade
  },
  data:() => ({
    mouseHover: false
  }),
  methods: {
    removeFile() {
      this.$emit("removeFile");
    },
    openLightbox() {
      this.$emit("openLightbox");
    },
    cancelUpload() {
      this.$emit("cancelUpload");
    },
  },
  computed: {
    wrapperClass() {
      let wrapperClass = "atch-file-wrapper"
      if (this.fileType === 'image') {
        wrapperClass = "image-content-wrapper"
        if (this.imageContain)
          wrapperClass += " contain"
      }
      else if (this.fileType === 'file')
        wrapperClass = "file-content-wrapper"
      else if (this.isImageFile)
        wrapperClass = "atch-image-wrapper"

      return wrapperClass
    },
    wrapperStyle() {
      if (this.fileType === 'image')
        return { 
          'height': commons.isNumber(this.imageHeight) ? `${this.imageHeight}px` : this.imageHeight,
          'width': commons.isNumber(this.imageWidth) ? `${this.imageWidth}px` : this.imageWidth
        } 
    },
    fileNameWithoutExtension() {
      if (Boolean(this.fileName && this.fileName.trim())) {
        const fileNameWithoutExtension = this.fileName.replace(/(.*)\.[^.]+$/, "$1");
        return Boolean(fileNameWithoutExtension.trim()) ? fileNameWithoutExtension : this.fileName;
      } else {
        return '(undefined)';
      }
    },
    fileColor() {
      return commons.getFileColor(this.fileName)
    },
    fileExtension() {
      return commons.getFileExtension(this.fileName)
    },
    isImageFile() {
      return commons.isImageFile(this.fileName)
    },
    getfileSize() {
      if (this.fileSize && typeof this.fileSize == 'number') {
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(this.fileSize) / Math.log(k));
        return parseFloat((this.fileSize / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
      }
    },
    getProgressPercentage() {
      return this.fileProgress && Math.trunc(this.fileProgress);
    },
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

/* to-do CSS naming */
.fts-file-content {
  position: relative;
  &.file-content-wrapper {
    width: 200px;
    display: flex;
    align-items: center;
    padding: 0.375rem;
    border: 1px solid var(--fts-border);
    background-color: var(--fts-card-background);
    line-height: normal;
    .file-extension {
      margin-right: 0.375rem;
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      border-radius: 0.125rem;
      background-color: var(--fts-default);
      color: var(--fts-light);
      font-weight: 400;
      font-size: 0.625rem;
      line-height: 1rem;
      white-space: nowrap;
      text-transform: uppercase;
    }
    .file-name {
      @include text-overflow-ellipsis;
      flex: 1;
      font-size: var(--inistate-label-text-14);
      line-height: var(--inistate-paragraph-text-14--line-height);
      text-decoration: none;
      color: var(--fts-text);
      &:hover {
        color: var(--fts-primary);
        cursor: pointer;
      }
    }
    .file-remove-action {
      margin-left: .375rem;
      font-size: var(--inistate-label-text-14);
      color: var(--fts-default);
      &:hover {
        color: var(--fts-danger);
        cursor: pointer;
      }
    }
  }
  &.image-content-wrapper {
    position: relative;
    display: inline-flex;
    vertical-align: bottom;
    &.contain > img {
      object-fit: contain;
    }
    &:not(.contain) > img {
      object-fit: cover;
    }
    > img {
      @include user-select-none;
      height: 100%;
      width: 100%;
      max-width: 100%;
      max-height: 100%;
    }
  }
  &.atch-image-wrapper {
    position: relative;
    .atch-image-content {
      min-width: 88px;
      position: relative;
      padding-top: 50px;
      overflow: hidden;
      cursor: zoom-in;
      > img {
        @include absolute-center-middle;
        max-width: 100px;
      }
    }
  }
  &.atch-file-wrapper {   
    height: 50px;
    width: 180px;
    max-width: 100%;
    display: flex;
    align-items: center;
    padding: 0.25rem;
    border: 1px solid var(--fts-border);
    background-color: var(--fts-card-background);
    .atch-file-content {
      flex: 1;
      @include flex-center-middle;
      min-width: 0;
      position: relative;
      text-decoration: none;
    }
    .atch-file-extension {
      position: relative;
      margin-right: 0.25rem;
      .atch-file-extension__icon {
        font-size: 2.375rem;
        color: var(--fts-default);
      }
      .atch-file-extension__name {
        width: 2.125rem;
        position: absolute;
        top: 60%;
        left: 50%;
        transform: translate(-50%, -50%);
        padding-left: 0.125rem;
        padding-right: 0.125rem;
        border-radius: 0.125rem;
        text-align: center;
        font-weight: 400;
        font-size: 0.625rem;
        line-height: 1rem;
        white-space: nowrap;
        text-transform: uppercase;
        color: var(--fts-light);
        overflow: hidden;
      }
    }
    .file-info {
      min-width: 0;
      flex: 1;
      .file-info__header {
        display: inline-flex;
        max-width: 100%;
      }
      .file-name {
        @include text-overflow-ellipsis;
        font-size: $font-size;
        color: var(--fts-text);
      }
      .file-size {
        font-size: $font-size-sm;
        color: var(--fts-default);
      }
    }
    .file-actions {
      position: absolute;
      right: 0;
      display: flex;
      align-items: center;
      padding: 0.125rem;
      box-shadow: var(--fts-elevation-1);
      border-radius: 0.25rem;
      background: var(--fts-popover-alt-background);
      .file-action {
        padding: 0.375rem;
        border-radius: inherit;
        font-size: 0.75rem;
        color: var(--fts-default);
        transition: background-color 0.3s ease-in-out;
        &:hover {
          background-color: var(--fts-gray-100);
          color: var(--fts-default);
          cursor: pointer;
        }
      }
    }
  }
  .file-progress {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: row-reverse;
    .file-progress-bar {
      height: 100%;
      background: rgba($color: #000, $alpha: .65);
      transition: width .3s;
    }
    .file-progress-percent {
      position: absolute;
      bottom: 0;
      width: 100%;
      padding: .275rem;
      font-size: .75rem;
      text-align: center;
      color: #fff;
      background: rgba($color: #000, $alpha: .25);
    }
  }
  .file-remove {
    @include flex-center-middle;
    position: absolute;
    top: 2px;
    right: 2px;
    height: 20px;
    width: 20px;
    border-radius: 10em;
    background-color: var(--fts-default);
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.16), 0 5px 15px 0 rgba(0, 0, 0, 0.12);
    color: var(--fts-light);
    cursor: pointer;
    &:hover {
      background: var(--fts-danger);
    }
    svg {
      font-size: var(--inistate-label-text-14);
    }
  }
}
</style>